/* eslint-disable no-use-before-define */
// @flow
import type Moment from "moment";
import { type TipoAtivo } from "../state";
import { ID_CONFIGURACAO_RELATORIO_MEDIDA_NOME } from "./constantes";
import { includesIn, isMultiplasMedidasSelecionadas } from "../util";
import { any, complement, prop, propEq, propIs } from "ramda";
import { MEDIDAS_SEM_CASAS_DECIMAIS } from "./constantes";

import {
  FORMATACAO_MOEDA_MILHARES,
  FORMATACAO_MOEDAS,
  ID_TIPO_CALCULO_VARIACAO_PERCENTUAL,
} from "./constantes";

export type Id = number;
export type ChaveEnum = string;

export type Traduzido<T> = T & { traducao: string };

export type ModoApp = "NORMAL" | "SERIES" | "EXCEL";

export type TipoListaPeriodo =
  | "DEFAULT"
  | "PERIODO_DIARIO"
  | "PONTUAL_CARTEIRA"
  | "PONTUAL_DIARIO"
  | "PONTUAL_MENSAL";

export type ModoExibicaoMedida = {
  chave:
    | "PERIODO"
    | "PERIODO_COM_ATIVO_SEM_RISCO"
    | "PERIODO_COM_BENCHMARK"
    | "PERIODO_COM_BENCHMARK_E_ATIVO_SEM_RISCO"
    | "PERIODO_COM_HCVL"
    | "PERIODO_COM_INDICES"
    | "PERIODO_COM_LAMBDA"
    | "PERIODO_DIARIO"
    | "PERIODO_DIARIO_COM_BENCHMARK"
    | "PERIODO_DIARIO_SEM_PERIODICIDADE"
    | "PERIODO_SEM_PERIODICIDADE"
    | "PONTUAL_CARTEIRA"
    | "PONTUAL_DIARIO"
    | "PONTUAL_DIARIO_DEFLATOR_CORRECAO"
    | "PONTUAL_DIARIO_COM_FLAG_ULTIMA_NEGOCIACAO"
    | "PONTUAL_DIARIO_DEFLATOR_CORRECAO_COM_FLAG_ULTIMA_NEGOCIACAO"
    | "PONTUAL_DIARIO_COM_INDICE"
    | "PONTUAL_DIARIO_SEM_PERIODICIDADE"
    | "PONTUAL_MENSAL"
    | "PONTUAL_MENSAL_DEFLATOR_CORRECAO"
    | "MEDIDA_QUALITATIVA"
    | "MEDIDA_USUARIO"
    | "BALANCO"
    | "ACUMULACAO"
    | "ACUMULACAO_PL"
    | "ACUMULACAO_CAPITAL"
    | "MERCADO"
    | "BALANCO_MERCADO"
    | "ACUMULACAO_MERCADO"
    | "BALANCO_DEFLATOR"
    | "ACUMULACAO_DEFLATOR"
    | "BALANCO_MERCADO_DEFLATOR"
    | "ACOES_PONTUAL_DIARIO"
    | "PONTUAL_MENSAL_ATIVO_COMPRADO"
    | "PONTUAL_MENSAL_COMPRADOR"
    | "PONTUAL_MENSAL_CARTEIRA_EM_NIVEIS"
    | "PERIODO_COM_LIMITE"
    | "PERIODO_COM_BENCHMARK_HC",
  tipoListaPeriodo: TipoListaPeriodo,
  modoComJanelaMovel: boolean,
};

export type FiltroTipoAtivo = {
  id: Id,
  nome: TipoAtivo,
  chaveTraducao: string,
  tiposAtivosRelacionados: Array<ChaveEnum>,
};

export type GrupamentosMedida =
  | "QUANTITATIVA"
  | "QUALITATIVA"
  | "SERIES"
  | "EMPRESAS"
  | "PERSONALIZADAS";

export type GrupamentoMedida = {
  nome: GrupamentosMedida,
  chaveTraducao: string,
};

export type TipoMedidasSecundarias = "DERIVADA" | "AGREGADA" | "PERIODO_SERIE";

export type TipoFormatacaoMedida =
  | "TEXTO"
  | "PORCENTAGEM"
  | "MOEDA_2_CASAS_DECIMAIS"
  | "MOEDA_6_CASAS_DECIMAIS"
  | "MOEDA_14_CASAS_DECIMAIS"
  | "NUMERO"
  | "DATA"
  | "CNPJ"
  | "INTEIRO"
  | "PORCENTAGEM_TAXAS"
  | "MOEDA_QUALITATIVA"
  | "MOEDA_5_CASAS_DECIMAIS"
  | "PORCENTAGEM_TAXAS_SEM_TEXTOS"
  | "PORCENTAGEM_2_CASAS"
  | "PORCENTAGEM_4_CASAS"
  | "PORCENTAGEM_TAXAS_SEM_TEXTOS_2_CASAS"
  | "CODIGO"
  | "SUSEP"
  | "MOEDA_QUALITATIVA_COM_TEXTO";

export type Medida = {
  idConfiguracaoRelatorio: Id,
  idGrupoMedidasSelecionadas?: number,
  codigoOriginal: number,
  nome: string,
  modoExibicao: ChaveEnum,
  modoExibicaoModoSeries: ChaveEnum,
  filtros: Array<Id>,
  grupamentoMedida: GrupamentoMedida,
  tipoMedidasSecundarias: ?TipoMedidasSecundarias,
  medidasSecundarias: ?Array<Medida>,
  idConfiguracaoRelatorioMedidaPrimaria: ?Id,
  conjuntoPermissao: number,
  formatacao: ?TipoFormatacaoMedida,
};

export type MedidaTraduzida = Traduzido<Medida>;

export type MedidaUsuarioTipo = {
  id: Id,
  descricao: string,
};

export type AjusteDias = {
  id: Id,
  chaveTraducao: string,
  chaveCalculo: string,
};

export type Periodo = {
  id: Id,
  chaveCalculo: string,
  chaveTraducao: string,
  periodicidades: Array<Id>,
};

export type ListaPeriodosPorTipo = {
  [TipoListaPeriodo]: Array<Id>,
};

export type Periodicidade = {
  id: Id,
  chaveTraducao: string,
};

export type EnumCodigoDescricao = {
  codigo: Id,
  descricao: string,
};

export type EnumIdChaveTraducao = {
  id: Id,
  chaveTraducao: string,
};

export type Indice = {
  nome: string,
  nomeTraduzido: string,
  datas: string,
};

export type BenchmarkPersonalizado = { nome: string, nomeTraduzido: string };

export type TipoIndice =
  | "OUTROS"
  | "RENDA_FIXA"
  | "RENDA_VARIAVEL"
  | "FUNDOS"
  | "INFLACAO"
  | "MOEDAS"
  | "FTSE"
  | "MSCI"
  | "COMMODITIES"
  | "MACRO_BCB"
  | "MACRO_FGV"
  | "MACRO_IBGE";

export type Deflator = {
  codigo: number,
  nome: string,
  tipoIndice: TipoIndice,
};

export type CorretorMonetario = {
  codigo: number,
  nome: string,
  tipoIndice: TipoIndice,
};

export type TaxaDeAdministracao = {
  id: Id,
  chaveTraducao: string,
  efetivo: boolean,
};

export type Volatilidade = {
  id: Id,
  descricao: string,
};

export type TipoDoRetorno = {
  id: Id,
  chaveTraducao: string,
};

export type MesEAnoComposto = {
  nome: string,
  nomeTraduzido: string,
};

export type AtivoSelecionado = {
  id: string,
  type: string,
};

export type VersaoConta = EnumCodigoDescricao & { versaoContaEnum: string };

export type ErrosSelecao = {
  dataInicialNaoSelecionada?: string,
  dataFinalNaoSelecionada?: string,
  dataInicialInvalida?: string,
  dataFinalInvalida?: string,
  dataInicialForaDosLimites?: string,
  dataFinalForaDosLimites?: string,
  periodoNaoSelecionado?: string,
  selecaoPeriodosInvalida?: string,
  selecaoDataEspecifica?: string,
  frequenciaNaoSelecionada?: string,
  rankingSemColunas?: string,
  rankingSemPesos?: string,
  medidaCompostaSemNome?: string,
  medidaCompostaSemFormato?: string,
  medidaCompostaSemExpressao?: string,
  medidaCompostaExpressaoInvalida?: string,
  campoNaoSelecionado?: string,
  ativoNaoSelecionado?: string,
  indicesNaoSelecionados?: string,
  ajustesPrecoNaoSelecionados?: string,
};

export type SelecaoJanela = {
  tamanhoJanela: ?number,
  quantidadeJanelas: ?number,
  tipoJanela: ?Id,
  ajusteDias: ?Id,
  dataBase: ?Moment,
  periodicidade?: ?Id,
};

export type DatasSelecaoJanela = {
  dataInicial: string,
  dataFinal: string,
  numeroDaJanela: number,
};

export type ChaveValorInteiro = {
  key: number,
  value: number,
};

export type FuncaoQtLink = {
  conteudo: string,
  carregando: boolean,
};

export type ArquivoMedida = {
  id: ?Id,
  nome: string,
  tipoArquivo: Id,
  dataModificacao: ?Date,
};

export type OperacaoArquivos =
  | "ABRIR"
  | "SALVAR"
  | "COMPARTILHAR"
  | "TEMPLATES";

export type ControleArquivos = {
  listaArquivos: Array<ArquivoMedida>,
  exibirModalArquivosMedidas: boolean,
  operacao?: OperacaoArquivos,
  extraInfoOperacao?: any,
  arquivoEmCache: ?ArquivoMedida,
  carregandoArquivo?: boolean,
  permiteCompartilhamento: boolean,
  +salvandoArquivo: boolean,
};

export type ArquivoConfiguracaoRelatorio = {
  medidasSelecionadas: Array<MedidaSelecionada>,
  configuracaoGlobalRelatorio: ConfiguracaoGlobalRelatorio,
};

export type UsuarioCompartilhamento = {
  id: Id,
  nome: string,
  empresa: string,
  logins: Array<string>,
};

export type GrupoCompartilhamento = {
  nome: string,
  usuarios: Array<UsuarioCompartilhamento>,
};

export type FuncaoRanking = {
  id: Id,
  funcao: "ranking" | "percentil" | "indice",
  chaveTraducao: string,
};

export type FuncaoRankingTraduzida = Traduzido<FuncaoRanking>;

export type DataBase = string;
export type TipoDataBase = "AUTOMATICA" | "FECHAMENTO";

export type FormatacaoMedidaComposta = {
  codigo: Id,
  nome: string,
  chaveTraducao: string,
};

export type CampoMedidaSecundaria = {
  idMedida: Id,
  chaveTraducao: string,
  checked: boolean,
};

export type VisualizacaoCarteira = {
  id: string,
  chaveTraducao: string,
};

export type RetornoNumerador = {
  id: string,
  chaveTraducao: string,
};

export type CalculoNumerador = {
  id: string,
  chaveTraducao: string,
};

export type PeriodicidadeEstatistica = {
  id: string,
  chaveTraducao: string,
};

export type TipoVolatilidade = {
  id: string,
  chaveTraducao: string,
};

export type EstimacaoVolatilidade = {
  id: string,
  chaveTraducao: string,
};

export type TipoRetornoEstatistica = {
  id: string,
  chaveTraducao: string,
};

export type TipoIdentificadorAtivo = {
  id: number,
  descricao: string,
};

export type TipoAjustePreco = {
  id: number,
  chaveTraducao: string,
};

export type EscalaMoeda = {
  id: number,
  chaveTraducao: string,
};

export type FonteFidc = {
  id: number,
  chaveTraducao: string,
};

export type FormaDeVisualizacaoSeries = {
  id: number,
  chaveTraducao: string,
};

export type ConfiguracaoGlobalRelatorio = {
  dataBase: string,
  tipoDataBase: TipoDataBase,
  periodicidade?: number,
  ajusteDias?: number,
  periodo?: number,
  dataInicial?: string,
  dataFinal?: string,
  exibirDatasSemNegociacao?: boolean,
  identificadoresAtivos?: Array<number>,
  yamlConfiguracao?: string,
  nomeGrupoAtivosAberto?: string,
  nomeGrupoMedidasAberto?: string,
};

export type Dominio = {
  +ajustesDias: Array<AjusteDias>,
  +filtrosTipoAtivo: Array<FiltroTipoAtivo>,
  +medidas: Array<Medida>,
  +periodicidades: Array<Periodicidade>,
  +periodos: Array<Periodo>,
  +periodosPorTipoLista: ListaPeriodosPorTipo,
  +modosExibicao: Array<ModoExibicaoMedida>,
  +tiposConta: Array<EnumCodigoDescricao>,
  +versoesConta: Array<VersaoConta>,
  +formasAcumulacao: Array<EnumCodigoDescricao>,
  +capitaisUtilizados: Array<EnumCodigoDescricao>,
  +anos: Array<EnumCodigoDescricao>,
  +tiposReferencia: Array<EnumCodigoDescricao>,
  +tiposDataReferencia: Array<EnumCodigoDescricao>,
  +trimestresReferencia: Array<EnumCodigoDescricao>,
  +frequeciasIndicador: Array<EnumCodigoDescricao>,
  +separadorDecimal: string,
  +taxasDeAdministracao: Array<TaxaDeAdministracao>,
  +volatilidades: Array<Volatilidade>,
  +tiposDoRetorno: Array<TipoDoRetorno>,
  +indices: Array<Indice>,
  +mesesEAnosCompostos: Array<MesEAnoComposto>,
  +plsUtilizado: Array<EnumCodigoDescricao>,
  +corretoresMonetarios: Array<CorretorMonetario>,
  +deflatores: Array<Deflator>,
  +valoresBaseDeflator: Array<EnumCodigoDescricao>,
  +datasBaseDeflator: Array<EnumCodigoDescricao>,
  +dataBaseDefault: DataBase,
  +dataBaseMaxima: DataBase,
  +tiposJanela: Array<EnumIdChaveTraducao>,
  +grupamentosMedida: Array<GrupamentoMedida>,
  +diasUteis: Array<string>,
  +benchmarksPersonalizados: Array<string>,
  +dataFechamentoMesAnterior: ?number,
  +trimestreEAnoBaseEmpresasAbertas: ?ChaveValorInteiro,
  +funcoesRanking: Array<FuncaoRanking>,
  +formatacaoMedidasCompostas: Array<FormatacaoMedidaComposta>,
  +camposMedidasSecundarias: Array<CampoMedidaSecundaria>,
  +formasVisualizacaoCarteira: Array<VisualizacaoCarteira>,
  +tiposRetornoNumerador: Array<RetornoNumerador>,
  +tiposCalculoNumerador: Array<CalculoNumerador>,
  +periodicidadeEstatistica: Array<PeriodicidadeEstatistica>,
  +tiposVolatilidade: Array<TipoVolatilidade>,
  +estimacaoVolatilidade: Array<EstimacaoVolatilidade>,
  +tiposRetornoEstatistica: Array<TipoRetornoEstatistica>,
  +tiposAjuste: Array<TipoAjustePreco>,
  +tiposAjusteSemProventos: Array<TipoAjustePreco>,
  +fontesDeDadosFIDC: Array<FonteFidc>,
  +escalas: Array<EscalaMoeda>,
  +formasDeVisualizacaoRelatorioSeries: Array<FormaDeVisualizacaoSeries>,
};

export type EdicaoMedida = {
  +medida: ?Id,
  +alias: ?string,
  +periodicidade?: ?Id,
  +ajusteDias: ?Id,
  +periodos: Array<Id>,
  +dataInicial?: ?Moment,
  +dataFinal?: ?Moment,
  +anos?: Array<Id>,
  +tipoReferencia?: ?Id,
  +trimestreReferencia?: ?Id,
  +anoReferencia?: ?Id,
  +comparacao?: ?Id,
  +tipoConta?: ?Id,
  +versaoConta?: ?Id,
  +formaAcumulacao?: ?Id,
  +capitalUtilizado?: ?Id,
  +toleranciaDF?: ?number,
  +lambda?: ?number,
  +ativoSemRisco?: ?string,
  +benchmark?: ?string,
  +taxaDeAdministracao?: ?Id,
  +horizonte?: ?number,
  +confianca?: ?number,
  +volatilidade?: ?number,
  +tipoDoRetorno?: ?number,
  +competencia?: ?string,
  +indice?: ?string,
  +indices?: ?Array<string>,
  +frequencias?: Array<Id>,
  +plUtilizado?: ?Id,
  +toleranciaDiasValorMercado?: number,
  +corretorMonetario?: ?number,
  +deflator?: ?number,
  +valorBaseDeflator?: ?Id,
  +valorBaseDeflatorOutro?: ?number,
  +dataBaseDeflator?: ?Id,
  +dataBaseDeflatorDataEspecifica?: ?Moment,
  +dataEspecifica?: ?Moment,
  +dataInicialComposta?: ?string,
  +dataFinalComposta?: ?string,
  +tamanhoJanela?: ?number,
  +quantidadeJanelas?: ?number,
  +tipoJanela?: ?Id,
  +datasUteisSelecaoJanela?: ?Array<DatasSelecaoJanela>,
  +mesesSelecaoJanela?: ?Array<DatasSelecaoJanela>,
  +valorComboMedidas: string,
  +indiceMedidaEditada?: ?number,
  +idGrupoMedidasSelecionadas?: ?Id,
  +tipoCalculo?: ?Id,
  +colunasRanking?: ?Array<Id>,
  +pesosRanking?: ?{ [string]: number },
  +expressao?: ?string,
  +nome?: ?string,
  +formato?: ?string,
  +ativos?: ?Array<{ id: Id, nome: string, tipoItemSelecionavel?: string }>,
  +campos?: ?Array<Id>,
  +visualizacaoCarteira?: ?string,
  +limite?: ?number,
  +retornoNumerador?: ?string,
  +calculoNumerador?: ?string,
  +periodicidadeNumerador?: ?string,
  +diasUteisPeriodicidadeNumerador?: ?number,
  +tipoVolatilidade?: ?string,
  +estimacaoVolatilidade?: ?string,
  +periodicidadeVolatilidade?: ?string,
  +diasUteisPeriodicidadeVolatilidade?: ?number,
  +tipoRetornoConfiguracoesEstatistica?: ?string,
  +idMedidaUsuario?: ?Array<Id>,
  +ajustesPreco?: ?Array<Id>,
  +ajustesPrecoSemProventos?: ?Array<Id>,
  +fonteFidc?: ?Id,
  +exibirConfiguracoesAvancadas?: boolean,
  +janelaMovel?: ?number,
  +escalas?: ?Array<EscalaMoeda>,
  +escala?: ?Id,
  +casasDecimais?: ?number,
  +ativos?: Array<string>,
  +ultimaNegociacaoMes?: boolean,
  +adicionarDataReferencia?: boolean,
};

export type MedidaSelecionada = {
  +medida: Medida,
  +alias: string,
  +label?: string,
  +descricaoSelecao: ?string,
  +indiceRegra?: number,
  +isChecked: boolean,
  +periodicidade?: ?Id,
  +ajusteDias?: ?Id,
  +periodo?: ?Id,
  +tipoConta?: ?Id,
  +versaoConta?: ?Id,
  +formaAcumulacao?: ?Id,
  +capitalUtilizado?: ?Id,
  +toleranciaDF?: ?number,
  +dataInicial?: Date | string,
  +dataFinal?: Date | string,
  +ano?: number,
  +tipoReferencia?: ?Id,
  +trimestreReferencia?: ?Id,
  +anoReferencia?: ?Id,
  +dataReferencia?: ?Id,
  +lambda?: ?number,
  +ativoSemRisco?: ?string,
  +benchmark?: ?string,
  +taxaDeAdministracao?: ?Id,
  +horizonte?: ?number,
  +confianca?: ?number,
  +volatilidade?: ?number,
  +tipoDoRetorno?: ?number,
  +competencia?: ?string,
  +indice?: ?string,
  +indices?: ?Array<string>,
  +frequencia?: ?Id,
  +plUtilizado?: ?Id,
  +toleranciaDiasValorMercado?: number,
  +correcaoMonetaria?: ?number,
  +deflator?: ?number,
  +valorBaseDeflator?: ?Id,
  +valorBaseDeflatorOutro?: ?number,
  +dataBaseDeflator?: ?Id,
  +dataBaseDeflatorDataEspecifica?: ?Moment,
  +dataEspecifica?: ?number,
  +dataInicialComposta?: ?string,
  +dataFinalComposta?: ?string,
  +tamanhoJanela?: ?number,
  +quantidadeJanelas?: ?number,
  +tipoJanela?: ?Id,
  +numeroJanela?: number,
  +tipoQuantidadeDiasReferencia?: ?Id,
  +indiceMedidaEditada?: ?number,
  +idGrupoMedidasSelecionadas?: ?Id,
  +expressao?: ?string,
  +formato?: ?string,
  +idAtivoComprado?: ?Id,
  +nomeAtivoComprado?: ?string,
  +tipoAtivoComprado?: ?string,
  +rankComprador?: ?string,
  +indiceUltimos4Meses?: ?number,
  +visualizacaoCarteira?: ?string,
  +limite?: ?number,
  +retornoNumerador?: ?string,
  +calculoNumerador?: ?string,
  +periodicidadeNumerador?: ?string,
  +diasUteisPeriodicidadeNumerador?: ?number,
  +tipoVolatilidade?: ?string,
  +estimacaoVolatilidade?: ?string,
  +periodicidadeVolatilidade?: ?string,
  +diasUteisPeriodicidadeVolatilidade?: ?number,
  +tipoRetornoConfiguracoesEstatistica?: ?string,
  +idMedidaUsuario?: ?Id,
  +janelaMovel?: ?number,
  +frequencias?: ?Array<number>,
  +tipoAjuste?: ?Id,
  +fonteDeDadosFIDC?: ?Id,
  +tipoCalculoAnaliseHorizontal?: ?Id,
  +escala?: ?Id,
  +casasDecimais?: ?number,
  +ativos?: Array<string>,
  +ultimaNegociacao?: boolean,
};

export type AtivoMedidaParaPersonalizar = {
  alias: string,
  nome: string,
  tipoItemSelecionavel: string,
};

export type MedidaMedidaParaPersonalizar = {
  alias: string,
  label: string,
};

export type MedidaParaPersonalizar = {
  id: string,
  +ativo?: ?AtivoMedidaParaPersonalizar,
  medida: MedidaMedidaParaPersonalizar,
};

export type ConfiguracaoDeRelatorio = {
  medidasSelecionadas: Array<MedidaSelecionada>,
  dataBase: DataBase,
  tipoDataBase: TipoDataBase,
};

export const isMedidaNome = (medida: Medida) =>
  medida.idConfiguracaoRelatorio === ID_CONFIGURACAO_RELATORIO_MEDIDA_NOME;

export const isMedidaNaoNumerica = (medida: Medida): boolean =>
  [
    "TEXTO",
    "DATA",
    "CNPJ",
    "PORCENTAGEM_TAXAS",
    "MOEDA_QUALITATIVA",
    "CODIGO",
    "SUSEP",
    "MOEDA_QUALITATIVA_COM_TEXTO",
  ].includes(medida.formatacao);

const gerarArrayDeFormatacoes = (medidaSelecionada: any) => {
  return propIs(Array, "medidasSecundarias", medidaSelecionada)
    ? prop("medidasSecundarias", medidaSelecionada).map(
        medida => medida.formatacao
      )
    : [];
};

export const isMedidaOuSecundariasComEscalaMoeda = (
  medidaSelecionada: any,
  tipoCalculoSelecionado: any
) => {
  const ESCALAS_MOEDA = FORMATACAO_MOEDAS.concat(FORMATACAO_MOEDA_MILHARES);
  return (
    tipoCalculoSelecionado !== ID_TIPO_CALCULO_VARIACAO_PERCENTUAL &&
    !isMultiplasMedidasSelecionadas(medidaSelecionada) &&
    medidaSelecionada &&
    (ESCALAS_MOEDA.includes(medidaSelecionada.formatacao) ||
      (propIs(Array, "medidasSecundarias", medidaSelecionada) &&
        complement(propEq)(
          "tipoMedidasSecundarias",
          "PERIODO_SERIE",
          medidaSelecionada
        ) &&
        any(includesIn(ESCALAS_MOEDA))(
          gerarArrayDeFormatacoes(medidaSelecionada)
        )))
  );
};

export const isMedidaOuSecundariasComCasasDecimais = (
  medidaSelecionada: any
) => {
  return (
    medidaSelecionada &&
    !isMultiplasMedidasSelecionadas(medidaSelecionada) &&
    (!MEDIDAS_SEM_CASAS_DECIMAIS.includes(medidaSelecionada.formatacao) ||
      (complement(propEq)(
        "tipoMedidasSecundarias",
        "PERIODO_SERIE",
        medidaSelecionada
      ) &&
        propIs(Array, "medidasSecundarias", medidaSelecionada) &&
        any(complement(includesIn(MEDIDAS_SEM_CASAS_DECIMAIS)))(
          gerarArrayDeFormatacoes()
        )))
  );
};
